import { useLogicBlocks } from '@morph-mapper/node-logic';
import { useGraphContext } from '../../context/graph/hook';
import { DrawerKind } from '../../store/ui';
import { t } from 'i18next';
import { useMemo } from 'react';
import { useStore } from '@/store';
import { useGraphNavigation } from '../../hooks';
import { NodeOptionsForm } from '../node-options-form/node-options-form';
import { NodeDerivationsPanel } from '../node-derivations-panel/node-derivations-panel';
import {
  PanelTabsLayout,
  PanelTabsLayoutTab,
  TemplateOutput,
} from '../../molecules';
import { createOptionViewModels } from '../../utils';
import { useNodeConfig } from './node-config-provider';

export const NodeConfigPanel = () => {
  const { nodeId } = useNodeConfig();
  const [variant, graphEvaluation] = useStore(({ config: cfg, graphs: g }) => [
    cfg.getVariant(),
    g.getGraphEvaluation,
  ]);
  const { logicBlocks } = useLogicBlocks(variant);
  const [closeDrawer, graphRef, graphId, INode, drawer] = useGraphContext(
    ({ ui, graph: g }) => [
      ui.closeDrawer,
      g.getRef(),
      g.getId(),
      g.node(nodeId),
      ui.drawer,
    ]
  );

  const { openGraph } = useGraphNavigation();

  const { title, options, icon: BlockIcon } = logicBlocks[INode.getKind()];

  const models = useMemo(
    () => createOptionViewModels(options, INode.getOptions(), graphEvaluation),
    [options, INode, graphEvaluation]
  );

  const handleOpenGraph = (key: string, graphOptionId?: string) => {
    openGraph({
      entryId: graphRef.fieldId,
      graphId: graphOptionId,
      state: {
        parentGraphId: graphId,
        ownerNodeId: nodeId,
        paramKey: key,
      },
    });
  };

  const buildTabs = () => {
    const tabs: PanelTabsLayoutTab[] = [];
    let defaultTab = 'template';

    if (models.isOk() && models.value.length > 0) {
      tabs.push({
        value: 'options',
        label: 'Options',
        content: (
          <NodeOptionsForm
            nodeId={nodeId}
            models={models.value}
            onOpenGraph={handleOpenGraph}
          />
        ),
      });

      tabs.push({
        value: 'derivations',
        label: 'Derivations',
        content: (
          <NodeDerivationsPanel
            models={models.value}
            onOpenGraph={handleOpenGraph}
          />
        ),
      });

      defaultTab = 'options';
    } else if (models.isErr()) {
      console.error('Failed to build node options:', models.error);
    }

    tabs.push({
      value: 'template',
      label: 'Template',
      content: <TemplateOutput nodeId={nodeId} />,
    });

    return { tabs, defaultTab };
  };

  const { tabs, defaultTab } = buildTabs();
  const requestedTab =
    drawer.kind === DrawerKind.NodeConfig ? drawer.tab : undefined;
  const activeTab =
    requestedTab && tabs.some((tab) => tab.value === requestedTab)
      ? requestedTab
      : defaultTab;
  return (
    <PanelTabsLayout
      title={t(title)}
      leftIcon={<BlockIcon />}
      onClose={closeDrawer}
      defaultTab={activeTab}
      tabs={tabs}
    />
  );
};
